import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  viewChild,
} from '@angular/core';
import { MatChipOption, MatChipSet } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { first, last } from 'lodash-es';
import { AggregatedSelectors } from 'projects/semeris/src/store/aggregated/aggregated.selector';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { BehaviorSubject, concat, merge, NEVER, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { FEEDBACK_TEMPLATE_MAP } from '../../../feedback/components/feedback-modal/feedback-modal.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { FastTooltipDirective } from '../../../shared/directives/fast-tooltip.directive';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { SafeHtmlPipe } from '../../../shared/pipes/safe-html.pipe';
import { IconButtonDirective } from '../../../ui/button/icon-button/icon-button.directive';
import { HelpComponentMode } from '../../models/help.model';
import { HelpService } from '../../services/help.service';
import { AiHelpComponent } from '../ai-help/ai-help.component';
import { GuidedToursComponent } from '../guided-tours/guided-tours.component';
import { HelpProductUpdatesComponent } from '../help-product-updates/help-product-updates.component';

export const HELP_ID_DEFAULT = 'main.html';

@Component({
  selector: 'semeris-help',
  imports: [
    CommonModule,

    PermissionsDirective,
    AiHelpComponent,
    GuidedToursComponent,
    HelpProductUpdatesComponent,
    IconButtonDirective,
    MatSlideToggle,
    HeaderComponent,
    MatIcon,
    FastTooltipDirective,
    LoaderComponent,
    MatChipSet,
    MatChipOption,
    SafeHtmlPipe,
  ],
  templateUrl: './help.component.html',
  styleUrls: ['./help.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpComponent implements OnInit, OnDestroy {
  private readonly helpService = inject(HelpService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  public readonly Permissions = Permission;
  public readonly HelpComponentMode = HelpComponentMode;

  public guidedTourParamsExtended$ = this.store.select(
    AggregatedSelectors.guidedTourParamsExtended
  );

  @HostBinding('class')
  @Input()
  public mode: HelpComponentMode = HelpComponentMode.Help;

  private backStack: string[] = [];
  private forwardStack: string[] = [];

  public readonly aiHelpComponent = viewChild(AiHelpComponent);

  public get hasBack(): boolean {
    return !!this.backStack.length && this.mode === HelpComponentMode.Help;
  }

  public get hasForward(): boolean {
    return !!this.forwardStack.length && this.mode === HelpComponentMode.Help;
  }

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      this.mode !== HelpComponentMode.GuidedTours &&
      target?.tagName?.toLocaleLowerCase() === 'a'
    ) {
      event.preventDefault();
      event.stopPropagation();

      const helpId = last(target.getAttribute('href').split('/'));

      if (helpId) {
        this.navigateTo(helpId);
      }
    }
  }

  public readonly helpContentEl = viewChild<ElementRef<HTMLDivElement>>('helpContentEl');

  @Input() public embedded = false;

  public help$: Observable<string>;

  public toc$: Observable<string>;

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @HostBinding('class.navigate-on-click')
  public navigateOnClick = false;

  protected readonly completed$ = new Subject();

  private helpId$: BehaviorSubject<string> = new BehaviorSubject<string>(HELP_ID_DEFAULT);

  public contextHelpEnabled$ = this.store.select(AppSelectors.contextHelpEnabled);

  public ngOnInit(): void {
    this.navigateOnClick = window.location.pathname.startsWith('/help');

    if (this.navigateOnClick) {
      concat(
        of(null),
        this.router.events.pipe(
          takeUntil(this.completed$),
          filter((event) => event instanceof NavigationEnd)
        )
      ).subscribe(() => {
        const helpId = window.location.pathname.replace(/\/help\/?/, '');
        this.helpId$.next(helpId || HELP_ID_DEFAULT);
      });
    } else {
      this.store
        .select(AppSelectors.helpId)
        .pipe(takeUntil(this.completed$))
        .subscribe((helpId) => {
          this.helpId$.next(helpId || HELP_ID_DEFAULT);
        });

      this.contextHelpEnabled$
        .pipe(
          takeUntil(this.completed$),
          switchMap((enabled) => {
            if (!enabled) {
              return NEVER;
            }

            return merge(
              this.store.select(AppSelectors.helpId),
              this.router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                  return this.helpService.getHelpIdByRoute(window.location.pathname);
                })
              )
            ).pipe(distinctUntilChanged());
          })
        )
        .subscribe((helpId) => {
          this.navigateTo(helpId);
        });
    }

    this.help$ = this.helpId$.pipe(
      map((helpId) => helpId || HELP_ID_DEFAULT),
      tap(() => {
        this.loading$.next(true);
      }),
      switchMap((helpId) =>
        this.helpService.getHelp(first(helpId.split('#'))).pipe(
          finalize(() => {
            this.loading$.next(false);
          })
        )
      ),
      tap(() => {
        setTimeout(() => {
          const hash =
            window.location.hash.replace('#', '') || last(this.helpId$.getValue()?.split('#'));

          document.querySelectorAll('.help-header-marked').forEach((el) => {
            el.classList.remove('help-header-marked');
          });

          if (hash && Number.isInteger(+hash)) {
            const headerId = +hash;

            const headerEl: HTMLHeadingElement = document.querySelector(
              headerId === 0
                ? `.external-help-content h1:first-of-type`
                : `.external-help-content h2:nth-of-type(${headerId})`
            );

            if (headerEl) {
              headerEl.classList.add('help-header-marked');
              headerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 100);
      })
    );

    this.toc$ = this.helpService.getHelp('toc.html');
  }

  public onClose() {
    this.store.dispatch(AppActions.ToggleHelp({ helpId: null, contextual: true, open: false }));
  }

  public onOpenInNewTab() {
    window.open(`/help/${this.helpId$.value}`, '_blank');
  }

  public onContextHelpToggleChange(checked: boolean) {
    this.store.dispatch(AppActions.SetContextualHelp({ enabled: !checked }));
  }

  public onOpenToc() {
    this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.Help }));
    this.navigateTo('toc.html');
  }

  public onHomeClick(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();

    this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.Help }));
    this.navigateTo(HELP_ID_DEFAULT);
  }

  public onBackClick(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();

    const helpId = this.backStack.pop();

    this.forwardStack.push(this.helpId$.value);

    this.navigate(helpId);
  }

  public onContactUsClick(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();

    this.store.dispatch(AppActions.OpenFeedback({ ...FEEDBACK_TEMPLATE_MAP.get('feedback') }));
  }

  public onForwardClick(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();

    const helpId = this.forwardStack.pop();

    this.backStack.push(this.helpId$.value);

    this.navigate(helpId);
  }

  public navigateTo(helpId: string) {
    this.backStack.push(this.helpId$.value);
    this.forwardStack = [];
    this.navigate(helpId);
  }

  public onAiHelpClick() {
    this.store.dispatch(
      AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.AiHelpMode })
    );

    setTimeout(() => {
      this.aiHelpComponent()?.focus();
    }, 100);
  }

  private navigate(helpId: string) {
    this.helpContentEl()?.nativeElement?.scrollTo(0, 0);
    if (this.navigateOnClick) {
      this.router.navigateByUrl(`/help/${helpId}`);
    } else {
      this.helpId$.next(helpId);

      if (this.mode !== HelpComponentMode.GuidedTours && this.mode !== HelpComponentMode.Updates) {
        this.store.dispatch(
          AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.Help })
        );
      }
    }
  }

  public onModeSelected(helpMode: HelpComponentMode) {
    this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode }));
  }

  public ngOnDestroy() {
    this.completed$.next(undefined);
    this.completed$.complete();
  }
}
